import { useEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { useTranslation } from 'react-i18next';
import { PDFJS_CMAP_URL, PDFJS_STANDARD_FONT_DATA_URL } from '@/constants/pdf';
import type { PdfDisplayMode } from './types';
import type { PdfViewerState } from './usePdfViewerState';

/** Loads the document and owns its lifecycle: cancellation, cleanup, reset. */
export function usePdfDocument(pdfUrl: string, state: PdfViewerState) {
  const { t } = useTranslation();
  const {
    setPdfDoc, setTotalPages, setCurrentPage, setLoading, setError,
    setSearchResults, setPdfSearchResults, setRenderedPages, setVisiblePages,
    canvasRefs, highlightLayerRefs, renderedPagesRef, renderingPagesRef,
    pdfDocRef, loadingTaskRef, searchAbortControllerRef, initialPageRef,
  } = state;

  // initialPage is deliberately absent from the deps: a page change must not
  // reload the document.
  useEffect(() => {
    let isCancelled = false;
    // Captured at effect start so cleanup does not read a later .current.
    const canvasRefsMap = canvasRefs.current;
    const highlightLayerRefsMap = highlightLayerRefs.current;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);

        setSearchResults([]);
        setPdfSearchResults([]);
        setRenderedPages(new Set());
        setVisiblePages(new Set());
        renderedPagesRef.current.clear();
        renderingPagesRef.current.clear();

        if (loadingTaskRef.current) {
          loadingTaskRef.current.destroy();
          loadingTaskRef.current = null;
        }

        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          cMapUrl: PDFJS_CMAP_URL,
          cMapPacked: true,
          standardFontDataUrl: PDFJS_STANDARD_FONT_DATA_URL,
          useSystemFonts: true,
        });
        loadingTaskRef.current = loadingTask;

        const pdf = await loadingTask.promise;

        if (isCancelled) {
          pdf.destroy();
          return;
        }

        if (pdfDocRef.current) {
          pdfDocRef.current.destroy();
        }

        setPdfDoc(pdf);
        pdfDocRef.current = pdf;
        setTotalPages(pdf.numPages);

        const targetPage = initialPageRef.current;
        if (targetPage >= 1 && targetPage <= pdf.numPages) {
          setCurrentPage(targetPage);
        } else {
          if (targetPage !== 1) {
            console.warn(`Invalid initialPage ${targetPage}, falling back to page 1`);
          }
          setCurrentPage(1);
        }
      } catch (err) {
        if (isCancelled) return;
        console.error('Error loading PDF:', err);
        setError(t('documents.pdfLoadFailed'));
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    if (pdfUrl) {
      loadPDF();
    }

    return () => {
      isCancelled = true;

      // Aborting before the document is destroyed keeps in-flight search work
      // from failing against a destroyed document.
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
        searchAbortControllerRef.current = null;
      }

      if (loadingTaskRef.current) {
        loadingTaskRef.current.destroy();
        loadingTaskRef.current = null;
      }

      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }

      canvasRefsMap.clear();
      highlightLayerRefsMap.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the load-error message; excluded so a language change doesn't reload the PDF and reset the current page (PR #790)
  }, [pdfUrl]);
}

interface InitialPageSyncArgs {
  state: PdfViewerState;
  initialPage: number;
  initialPageExplicit: boolean;
  preferredSearchPage?: number;
  displayMode: PdfDisplayMode;
  scrollToPage: (page: number, behavior?: ScrollBehavior, block?: 'start' | 'center') => void;
}

/** Navigates on an initialPage prop change without reloading the document. */
export function useInitialPageSync({
  state,
  initialPage,
  initialPageExplicit,
  preferredSearchPage,
  displayMode,
  scrollToPage,
}: InitialPageSyncArgs) {
  const {
    pdfDoc, totalPages, setCurrentPage,
    initialPageRef, initialPageExplicitRef, preferredSearchPageRef,
    pendingInitialScrollPageRef,
  } = state;

  useEffect(() => {
    initialPageRef.current = initialPage;
    initialPageExplicitRef.current = initialPageExplicit;
    preferredSearchPageRef.current = preferredSearchPage;
    if (initialPage > 1) {
      pendingInitialScrollPageRef.current = initialPage;
    }
    if (pdfDoc && initialPage >= 1 && initialPage <= totalPages) {
      setCurrentPage(initialPage);
      if (displayMode === 'scroll') {
        scrollToPage(initialPage, 'auto', 'start');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs and setters are stable
  }, [initialPage, initialPageExplicit, preferredSearchPage, pdfDoc, totalPages, displayMode, scrollToPage]);
}
